import { useEffect, useRef, useState } from 'react';

/**
 * Keeps a popup mounted for its closing animation, so it fades out instead of vanishing.
 *
 * Pass the popup's open flag; render while `mounted` is true, and add `is-closing` (see
 * popMotion.css) while `closing` is true. Every close path can keep setting the open flag to
 * false as before: this follows it, `closeMs` behind.
 *
 *   const pop = usePopTransition(menuOpen);
 *   {pop.mounted && <div className={`my-menu ui-pop${pop.closing ? ' is-closing' : ''}`}>...</div>}
 *
 * closeMs must match the `ui-pop-out` duration in popMotion.css (0.14s).
 */
export const POP_CLOSE_MS = 140;

export default function usePopTransition(open, closeMs = POP_CLOSE_MS) {
  const [mounted, setMounted] = useState(Boolean(open));

  useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    const timer = setTimeout(() => setMounted(false), closeMs);
    return () => clearTimeout(timer);
  }, [open, closeMs]);

  return { mounted: Boolean(open) || mounted, closing: !open && mounted };
}

/** Closing duration for .ui-modal and .ui-rise (popMotion.css). */
export const MODAL_CLOSE_MS = 160;

/**
 * usePopTransition for something whose contents are wiped as it closes.
 *
 * Most modals reset their state on close (agent: null, timeslots: []), so a fade-out would show
 * an emptied form. This holds the last value seen while open and returns it during the fade:
 *
 *   const edit = usePopPresence(editModal, editModal.open, MODAL_CLOSE_MS);
 *   {edit.mounted && <Overlay closing={edit.closing}>{edit.value.agent.username}</Overlay>}
 */
export function usePopPresence(value, open, closeMs = POP_CLOSE_MS) {
  const pop = usePopTransition(open, closeMs);
  const held = useRef(value);
  if (open) held.current = value;
  return { ...pop, value: open ? value : held.current };
}
